import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export default function FAQPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);


  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/public/faqs`);
        if (response.data.items?.length > 0) {
          setFaqs(response.data.items);
        } else {
          setFaqs([]);
        }
      } catch (error) {
        setFaqs([]);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16" data-testid="faq-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] to-[#050505]" />
        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p variants={fadeInUp} className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">
              {t('faq.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white">
              {t('faq.title')}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-dark py-16" data-testid="faq-list">
        <div className="container-premium max-w-3xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="space-y-4"
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="premium-card overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                  data-testid={`faq-${index}`}
                >
                  <span className="text-white font-medium pr-4">
                    {faq.question?.[lang] || faq.question?.es}
                  </span>
                  <ChevronDown 
                    size={20} 
                    className={`text-[#D4AF37] flex-shrink-0 transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="px-6 pb-5"
                  >
                    <p className="text-white/60 leading-relaxed">
                      {faq.answer?.[lang] || faq.answer?.es}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-subtle" data-testid="faq-cta">
        <div className="container-premium text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
              <HelpCircle size={32} className="text-[#D4AF37]" />
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-display text-3xl text-white mb-4">
              ¿No encuentras lo que buscas?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/60 mb-8">
              Contáctanos directamente y responderemos todas tus dudas
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href="mailto:info@gsagency.com" className="btn-gold">
                Escribenos
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
